import { cn } from "@/lib/utils"

/** Mark paths from `assets/logo.svg` (ArtlessDev "AD" lockup). */
function LogoMark({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 1500 1500" className={cn("block shrink-0", className)}>
      <path
        className="fill-current"
        d="M 528.355469 733.9375 L 683.296875 733.9375 L 528.339844 318.460938 L 362.421875 318.460938 L 211.199219 723.933594 L 40.527344 1181.535156 L 195.460938 1181.535156 L 366.132812 723.933594 L 445.378906 511.445312 L 528.355469 733.9375"
      />
      <path
        className="fill-current"
        d="M 1027.917969 318.460938 L 622.132812 318.460938 L 854.273438 940.882812 L 1009.203125 940.882812 L 831.203125 463.617188 L 1027.917969 463.617188 C 1186.078125 463.617188 1314.300781 591.839844 1314.300781 749.996094 C 1314.300781 908.15625 1186.078125 1036.378906 1027.917969 1036.378906 L 796.097656 1036.378906 L 753.941406 923.339844 L 753.921875 923.339844 L 718.929688 829.496094 L 434.898438 829.496094 L 386.324219 974.652344 L 618.136719 974.652344 L 695.296875 1181.535156 L 1035.019531 1181.535156 L 1034.988281 1181.453125 C 1270.054688 1177.675781 1459.457031 985.972656 1459.457031 749.996094 C 1459.457031 511.660156 1266.261719 318.460938 1027.917969 318.460938"
      />
    </svg>
  )
}

/** Coordinated mark + wordmark sizing. The mark's source paths only fill
 *  ~58% of their square viewBox height (an "AD" monogram is wider than it
 *  is tall), so sizing the mark box to the wordmark's line-box height reads
 *  as small and thin next to the bold wordmark — the mark is sized up
 *  ~12% relative to the wordmark box, and the gap tightened, so the two
 *  read as one deliberate lockup instead of a small icon floating next to
 *  text. Vertical alignment between the two is left to `items-center` on
 *  the parent: both the mark's ink and the wordmark's cap-height ink are
 *  already centered on their own box's midpoint, so centering the boxes
 *  centers the ink. */
const LOGO_LOCKUP = {
  md: {
    mark: "size-8",
    wordmarkBox: "h-7",
    wordmarkText: "text-[1.1875rem]",
    gap: "gap-2",
  },
  lg: {
    mark: "size-9",
    wordmarkBox: "h-8",
    wordmarkText: "text-[1.3125rem]",
    gap: "gap-2",
  },
  xl: {
    mark: "size-11",
    wordmarkBox: "h-10",
    wordmarkText: "text-[1.5rem]",
    gap: "gap-2.5",
  },
} as const

export type ArtlessDevLogoSize = keyof typeof LOGO_LOCKUP

export function ArtlessDevLogo({
  className,
  size = "md",
  markClassName,
  wordmarkClassName,
  showWordmark = true,
}: {
  className?: string
  size?: ArtlessDevLogoSize
  markClassName?: string
  wordmarkClassName?: string
  showWordmark?: boolean
}) {
  const lockup = LOGO_LOCKUP[size]

  return (
    <span className={cn("inline-flex items-center text-foreground", lockup.gap, className)}>
      <LogoMark className={cn(lockup.mark, markClassName)} />
      {showWordmark ? (
        <span
          className={cn(
            "font-display flex items-center leading-none font-semibold tracking-tight",
            lockup.wordmarkBox,
            lockup.wordmarkText,
            wordmarkClassName,
          )}
        >
          ArtlessDev
        </span>
      ) : null}
    </span>
  )
}
